import React, { useState } from 'react';
import { formatPrice, groupTests } from '../../../utils/labTestUtils';
import { labTestAPI } from '../../../services/api';
import Icon from '../../Icon';

export default function PlanCatalogStep({
  currentStep,
  skuList,
  catalogLoading,
  catalogError,
  isLastPage,
  expandedSku,
  setExpandedSku,
  selectedSku,
  setSelectedSku,
  fetchCatalog,
  handleLoadMore,
}) {
  // panel-pricing (fetchCatalog, above) only returns a test COUNT per plan —
  // sku.testsIncluded starts empty for all of them (see useLabTestBooking.js).
  // The itemised list is fetched here, lazily, only for whichever plan the
  // navigator actually expands — keyed by sku.id (== skuId, the same key
  // labTestAPI.getPackageDetails expects) so switching between two already-
  // expanded plans doesn't refetch.
  const [detailsBySkuId, setDetailsBySkuId] = useState({});
  const [loadingSkuId, setLoadingSkuId] = useState(null);
  const [detailsErrorBySkuId, setDetailsErrorBySkuId] = useState({});

  function loadDetails(sku) {
    setLoadingSkuId(sku.id);
    setDetailsErrorBySkuId(prev => ({ ...prev, [sku.id]: null }));
    labTestAPI.getPackageDetails(sku.id)
      .then(res => {
        setDetailsBySkuId(prev => ({ ...prev, [sku.id]: res.data?.testsIncluded || [] }));
      })
      .catch(() => {
        setDetailsErrorBySkuId(prev => ({ ...prev, [sku.id]: true }));
      })
      .finally(() => setLoadingSkuId(null));
  }
  return (
    <div className="form-section fade-in-up delay-2">
      <div className="form-section-title">
        <span className={`lab-step-badge${currentStep > 2 ? ' is-done' : ''}`}>2</span>
        Choose a test plan
      </div>
      <div className="form-section-subtitle">
        Tap a plan to select it. Home collection plans show a house badge.
      </div>

      {catalogError && (
        <div className="alert alert-error" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}><Icon name="alert" size={14} /> {catalogError}</span>
          {!catalogLoading && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fetchCatalog(1, false)}>
              ↺ Retry
            </button>
          )}
        </div>
      )}

      {catalogLoading && skuList.length === 0 ? (
        <div className="loading-state" style={{ padding: '40px 0' }}>
          <div className="spinner" />Loading plans…
        </div>
      ) : skuList.length === 0 ? (
        <div className="empty-state" style={{ padding: '40px 0' }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
          <div className="empty-title">No plans found</div>
          <div className="empty-desc">No packages match for this patient. Try again later or contact support.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {skuList.map(sku => {
            const isSelected   = selectedSku?.id === sku.id;
            const isExpanded   = expandedSku === sku.id;
            const testCount    = sku.noOfTestsIncluded ?? sku.testsIncluded?.length ?? 0;
            const fetchedTests = detailsBySkuId[sku.id];
            const isLoadingDetails = loadingSkuId === sku.id;
            const detailsFailed    = !!detailsErrorBySkuId[sku.id];
            const groups = groupTests(fetchedTests || sku.testsIncluded);
            return (
              <div
                key={sku.id}
                role="button"
                tabIndex={0}
                className={`lab-plan-card${isSelected ? ' is-selected' : ''}`}
                onClick={() => setSelectedSku(isSelected ? null : sku)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedSku(isSelected ? null : sku);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{sku.name}</div>
                      {sku.flags?.isFastingRequired && (
                        <span className="badge badge-pending" style={{ fontSize: 10 }}>Fasting Required</span>
                      )}
                      {sku.flags?.isHomeCollectible && (
                        <span className="badge badge-confirmed" style={{ fontSize: 10 }}>Home Collection</span>
                      )}
                    </div>
                    {/* DEF-005: this line used to append the partner's raw SKU id
                        and type code — rendering as "… · AAROGYAM 1.1 · SSKU".
                        "SSKU" is a Thyrocare-internal item-type enum that means
                        nothing to a navigator, and the id is a partner reference,
                        not a plan name; the same class of leak as the raw enum
                        token DEF-039 removed from the address chip. Both are kept
                        on the element's title so support can still read them on
                        hover without putting them in the visible UI. */}
                    <div
                      style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}
                      title={`Partner reference: ${sku.id}${sku.type ? ` (${sku.type})` : ''}`}
                    >
                      {sku.noOfTestsIncluded ?? sku.testsIncluded?.length ?? 0} tests included
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div className="lab-plan-price">{formatPrice(sku.rate)}</div>
                    {sku.rate?.discountPercentage && Number(sku.rate.discountPercentage) > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--green)' }}>{sku.rate.discountPercentage}% off</div>
                    )}
                  </div>
                </div>

                <div className="lab-plan-actions">
                  <span className={`badge ${isSelected ? 'badge-active' : 'badge-pending'}`}>
                    {isSelected ? '✓ Selected' : 'Tap card to select'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      const opening = !isExpanded;
                      setExpandedSku(opening ? sku.id : null);
                      // Fetch once per sku, on first expand — not on every
                      // toggle, and not for plans the navigator never opens.
                      if (opening && !fetchedTests && !isLoadingDetails) loadDetails(sku);
                    }}
                  >
                    {isExpanded ? '▲ Hide tests' : `▼ View ${testCount} tests`}
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--border)' }}>
                    {isLoadingDetails ? (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="spinner" style={{ width: 14, height: 14 }} />
                        Loading test list…
                      </div>
                    ) : detailsFailed ? (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        Couldn't load the test list.
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => { e.stopPropagation(); loadDetails(sku); }}
                        >
                          ↺ Retry
                        </button>
                      </div>
                    ) : groups.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No test details available.</div>
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: 12,
                      }}>
                        {groups.map(g => (
                          <div key={g.groupName} style={{
                            background: 'var(--bg-subtle)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm, 6px)',
                            padding: '10px 12px',
                          }}>
                            <div style={{
                              fontSize: 11, fontWeight: 700, color: 'var(--brand-blue)',
                              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8,
                            }}>
                              {g.groupName} <span style={{ color: 'var(--text-muted)' }}>({g.tests.length})</span>
                            </div>
                            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                              {g.tests.map(t => (
                                <li
                                  key={`${g.groupName}-${t.code}-${t.name}`}
                                  style={{ fontSize: 12, padding: '3px 0', display: 'flex', gap: 6 }}
                                >
                                  <span style={{ color: 'var(--green)' }}>•</span>
                                  <span style={{ flex: 1 }}>{t.name}</span>
                                  <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                    {t.code}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {!isLastPage && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleLoadMore}
              disabled={catalogLoading}
              style={{ alignSelf: 'center', marginTop: 4 }}
            >
              {catalogLoading ? 'Loading…' : '↓ Load more plans'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
